import React, { useState } from 'react';
import {
  AutoComplete, Input, Icon, Spin,
} from 'antd';
import * as PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import routeLocations from '../../utility/route-locations';
import './ViaSearch.css';
import { requestViaSearch } from '../../state/ui/application/viaSearchUIActions';
import { selectSearchResult, selectSearchValue } from '../../state/ui/application/viaSearchUIReducer';

const { Option, OptGroup } = AutoComplete;

const searchWaitMs = 400;
const inspectionGroupKey = 'inspection';
const keyDelimiter = '~';
const noActionKey = '*';


/**
 * A result value that is displayed in the summary component.
 * This highlight data that matches.
 * @param {string} props.searchValue The string being searched.
 * @param {string} props.children The child object. This should be a string.
 */
function ResultValue({ searchValue, children }) {
  const isMatch = searchValue.replace(/ /g, '') === `${children}`.replace(/ /g, '');
  if (!isMatch) return children;

  return (
    <strong>
      {children}
    </strong>
  );
}

ResultValue.propTypes = {
  searchValue: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
};


/**
 * The summary of an inspection result in the results drop down.
 * @param {object} props.summary The inspection summary object.
 * @param {string} props.string The value being searched. Used for highlighting matches.
 */
function InspectionResult({ summary, searchValue }) {
  return (
    <div className="inspection-result">
      <div className="inspection-result--key">
        {summary.inspectionCode}
      </div>
      <div className="inspection-result--summary">
        <div className="inspection-result--title">
          <FormattedMessage
            id="inspectionResult"
            values={{
              regNo: <ResultValue searchValue={searchValue}>{summary.regNumber}</ResultValue>,
              time: <ResultValue searchValue={searchValue}>{new Date(summary.inspectionDateTime).toLocaleString()}</ResultValue> }}
          />
        </div>
        <div className="inspection-result--keywords">
          <div className="inspection-result--keyword">
            <span className="inspection-result--keyword-label"><FormattedMessage id="inspectionReference" /></span>
            <span className="inspection-result--keyword-value"><ResultValue searchValue={searchValue}>{summary.inspectionCode}</ResultValue></span>
          </div>
          <div className="inspection-result--keyword">
            <span className="inspection-result--keyword-label"><FormattedMessage id="yourReference" /></span>
            <span className="inspection-result--keyword-value"><ResultValue searchValue={searchValue}>{summary.externalContextReference ? summary.externalContextReference : <FormattedMessage id="noData" />}</ResultValue></span>
          </div>
        </div>
      </div>
    </div>
  );
}

InspectionResult.propTypes = {
  summary: PropTypes.shape({}).isRequired,
  searchValue: PropTypes.string.isRequired,
};


/**
 * Creates all the inspection option components that are rendered inside of an OptionGroup component.
 * @param {Object[]} inspectionResults An array of inspection summaries.
 * @param {boolean} inspectionResultsLoading True if the inspection results are being loaded.
 * @param {string} searchValue The value being searched.
 */
const getInspectionOptions = (inspectionResults, inspectionResultsLoading, searchValue) => {
  if (inspectionResultsLoading) return <Option key={`${inspectionGroupKey}${keyDelimiter}${noActionKey}`} disabled><Spin /></Option>;
  if (!inspectionResults || inspectionResults.length <= 0) return <Option key={`${inspectionGroupKey}${keyDelimiter}${noActionKey}`} disabled>No Results</Option>;

  return inspectionResults.map(inspection => (
    <Option key={`${inspectionGroupKey}${keyDelimiter}${inspection.inspectionCode}`} label={searchValue}>
      <InspectionResult summary={inspection} searchValue={searchValue} />
    </Option>
  ));
};

/**
 * The global application search component. This will search the application for important items that
 * match the search string.
 * @param {object} props All unused props are passed to the child AutoComplete component.
 * @param {object} props.history (HOC) The react-router history object.
 * @param {string} props.searchValue (Redux) The value being searched in the redux state.
 * @param {function} props.doSearch (Redux) The function for dispatching a search action. (string) => void
 * @param {Object[]} props.inspectionResults (Redux) The inspection results in the redux state.
 * @param {boolean} props.inspectionResultsLoading (Redux) True if the inspection results are still being loaded from the server.
 */
function ViaSearch({
  history, searchValue, doSearch, inspectionResults, inspectionResultsLoading, ...rest
}) {
  const [currentValue, setCurrentValue] = useState();
  const [searchTimeoutId, setSearchTimeoutId] = useState(null);

  // The inspection group component.
  const inspectionOptions = () => (
    <OptGroup key={inspectionGroupKey} label={<FormattedMessage id="inspection" />}>
      { getInspectionOptions(inspectionResults, inspectionResultsLoading, searchValue) }
    </OptGroup>
  );

  // When the search string has been changed.
  const onChange = (value) => {
    setCurrentValue(value);
    if (searchTimeoutId) clearTimeout(searchTimeoutId);

    // Wait a short moment after key press before searching.
    const timeoutId = setTimeout(() => {
      doSearch(value);
    }, searchWaitMs);
    setSearchTimeoutId(() => timeoutId);
  };

  // When a search result has been selected.
  const onSelect = (key) => {
    // Handle an inspection search.
    if (key.startsWith(`${inspectionGroupKey}`)) {
      const inspectionCode = key.slice(key.indexOf(keyDelimiter) + 1, key.length);
      history.push(`${routeLocations.inspection}/${inspectionCode}`);
    }
    onChange(); // Reset the search state.
  };

  return (
    <AutoComplete
      value={currentValue}
      placeholder={<FormattedMessage id="findInspections" />}
      onChange={onChange}
      onSelect={onSelect}
      optionLabelProp="label"
      dataSource={[inspectionOptions()]}
      {...rest}
    >
      <Input
        suffix={<Icon type="search" style={{ color: 'var(--via-header-text-colour)' }} />}
        maxLength={20}
      />
    </AutoComplete>
  );
}

ViaSearch.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  searchValue: PropTypes.string,
  doSearch: PropTypes.func.isRequired,
  inspectionResults: PropTypes.arrayOf(PropTypes.shape({})),
  inspectionResultsLoading: PropTypes.bool.isRequired,
};

ViaSearch.defaultProps = {
  searchValue: null,
  inspectionResults: null,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const searchValue = selectSearchValue(state);
  const searchResult = selectSearchResult(state);
  const inspectionSearchResults = searchResult ? searchResult.inspectionSearchResults : null;

  return {
    searchValue,
    inspectionResultsLoading: inspectionSearchResults ? inspectionSearchResults.isLoading : false,
    inspectionResults: inspectionSearchResults ? inspectionSearchResults.results : [],
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  doSearch: searchValue => dispatch(requestViaSearch(searchValue)),
});


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ViaSearch));
